import React, { useMemo } from 'react';

/**
 * Sparkline SVG senza librerie di grafici (vietate dal CLAUDE.md di progetto).
 *
 * Serve a rispondere a una domanda che una tabella di numeri non risponde a
 * colpo d'occhio: "sta cambiando qualcosa?". Un dump che si dimezza e un
 * ripristino che rallenta di giro in giro sono entrambi esiti OK, e in colonna
 * non si notano.
 *
 * ⚠️ ONESTA' DELLA SCALA. L'asse Y parte dal minimo della serie, non da zero:
 * su valori che oscillano dell'1% (la dimensione di un dump fra due giri
 * consecutivi) una scala da zero mostrerebbe una riga piatta e inutile. Il
 * rovescio e' che una variazione minuscola sembra una montagna, quindi i
 * valori minimo e massimo sono SEMPRE scritti accanto al grafico: il disegno
 * da' la forma, i numeri danno la misura. Non togliere quelle etichette.
 */

export interface PuntoSparkline {
  /** null quando il giro e' fallito o il dato non e' stato registrato. */
  valore: number | null;
  /** Testo del tooltip nativo: data e valore formattato. */
  etichetta: string;
  /** Giro fallito: viene marcato in rosso sulla linea di base. */
  errore?: boolean;
}

interface Props {
  /** In ordine cronologico: il piu' vecchio per primo. */
  punti: PuntoSparkline[];
  /** Formattazione delle etichette di minimo e massimo. */
  formato: (valore: number) => string;
  /** Descrizione per i lettori di schermo, a cui il disegno non arriva. */
  descrizione: string;
}

// Spazio di coordinate interno: l'SVG scala con la larghezza disponibile
// mantenendo le proporzioni, quindi questi numeri non sono pixel.
const W = 600;
const H = 72;
const PAD_Y = 10;      // aria sopra e sotto, o la linea tocca i bordi
const PAD_DX = 6;      // margine laterale, o il primo e l'ultimo punto sono tagliati

export default function BopSparkline({ punti, formato, descrizione }: Props) {
  const calcolo = useMemo(() => {
    const valori = punti.map(p => p.valore).filter((v): v is number => v !== null && Number.isFinite(v));
    if (valori.length === 0) return null;

    const min = Math.min(...valori);
    const max = Math.max(...valori);

    // Serie piatta (tutti i valori uguali): senza questo caso la divisione per
    // (max - min) darebbe NaN e il grafico spariva. Va disegnata a metá altezza,
    // che e' anche la lettura giusta: non e' cambiato niente.
    const piatta = max === min;

    const x = (i: number) => punti.length === 1
      ? W / 2
      : PAD_DX + (i * (W - PAD_DX * 2)) / (punti.length - 1);

    const y = (v: number) => piatta
      ? H / 2
      : H - PAD_Y - ((v - min) / (max - min)) * (H - PAD_Y * 2);

    // I punti nulli interrompono la linea invece di essere interpolati: unire
    // i due lati di un backup fallito disegnerebbe una continuita' che non c'e'.
    //
    // Un valore isolato fra due interruzioni non produce alcun segmento (una
    // polyline di un punto non e' una linea) e resta visibile solo grazie al
    // suo pallino, disegnato piu' sotto. E' voluto: il dato si vede, la linea
    // che non c'e' non viene inventata.
    const segmenti: string[] = [];
    let corrente: string[] = [];
    punti.forEach((p, i) => {
      if (p.valore === null || !Number.isFinite(p.valore)) {
        if (corrente.length > 1) segmenti.push(corrente.join(' '));
        corrente = [];
        return;
      }
      corrente.push(`${x(i).toFixed(1)},${y(p.valore).toFixed(1)}`);
    });
    if (corrente.length > 1) segmenti.push(corrente.join(' '));

    return { min, max, piatta, x, y, segmenti };
  }, [punti]);

  if (!calcolo) {
    return <div className="bop-sparkline__vuoto">Nessun dato da mostrare</div>;
  }

  const { min, max, piatta, x, y, segmenti } = calcolo;

  return (
    <div className="bop-sparkline">
      <svg
        className="bop-sparkline__svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={descrizione}
      >
        {segmenti.map((s, i) => (
          <polyline key={i} className="bop-sparkline__linea" points={s} />
        ))}

        {punti.map((p, i) => {
          // Giro fallito: pallino rosso sulla linea di base. Sta in basso e non
          // sulla linea perche' un backup fallito non ha un valore da mostrare —
          // metterlo a un'altezza qualsiasi sarebbe inventarsi un dato.
          if (p.valore === null || !Number.isFinite(p.valore)) {
            return (
              <circle
                key={i}
                className="bop-sparkline__punto bop-sparkline__punto--errore"
                cx={x(i)}
                cy={H - 3}
                r={3}
              >
                <title>{p.etichetta}</title>
              </circle>
            );
          }
          return (
            <circle
              key={i}
              className={`bop-sparkline__punto${p.errore ? ' bop-sparkline__punto--errore' : ''}${
                i === punti.length - 1 ? ' bop-sparkline__punto--ultimo' : ''
              }`}
              cx={x(i)}
              cy={y(p.valore)}
              r={i === punti.length - 1 ? 3.5 : 2.5}
            >
              <title>{p.etichetta}</title>
            </circle>
          );
        })}
      </svg>

      {/* I numeri della scala. Vedi l'avvertenza in testa al file: senza questi
          il disegno non e' interpretabile. */}
      <div className="bop-sparkline__scala">
        {piatta ? (
          <span className="bop-sparkline__scala-valore">costante · {formato(max)}</span>
        ) : (
          <>
            <span className="bop-sparkline__scala-valore">max {formato(max)}</span>
            <span className="bop-sparkline__scala-valore">min {formato(min)}</span>
          </>
        )}
      </div>
    </div>
  );
}
